import Rectangle from '../utils/Rectangle'
import { snapThreshold } from 'variables'

/**
 * find the center of the area overlapping region 
 */
function centerOverlap(a1, a2, b1, b2) {
  const dist1 = a1 - b1
  const dist2 = a2 - b2

  if (dist1 >= 0 && dist2 <= 0) {
    // a is fully inside b
    // return a's midpoint
    return (a1 + a2) / 2
  } else if (dist1 <= 0 && dist2 >= 0) {
    // b is fully inside a
    // return b's midpoint
    return (b1 + b2) / 2
  } else if (dist1 <= 0 && dist2 <= 0) {
    // b is partly after a
    // return midpoint of overlap
    return (b1 + a2) / 2
  } else if (dist1 >= 0 && dist2 >= 0) {
    // b is partly before a
    // return midpoint of overlap
    return (a1 + b2) / 2
  } else {
    return a1 // crude fallback
  }
}

/**
 * Base class for different types of snap point
 */
class SnapPoint {
  getDistance(block) {
    return Math.abs(block.top - this.top)
  }

  getTop() {
    return this.top
  }
}

class SnapAlign extends SnapPoint {
  // Dragged block's top or bottom can snap to this

  constructor({ id, top }) {
    super()

    this.blockIds = [id]
    this.top = top
    this.getGuides = draggedBlock => [{ type: 'hRule', top }]
  }

  getTop(layout) {
    // Dragged block's top or bottom can snap to this
    const block = new Rectangle(layout)
    const top = Math.abs(block.top - this.top)
    const bottom = Math.abs(block.bottom - this.top)
    // snap to closest point, top or bottom
    if (top < bottom) return this.top
    return this.top - block.height
  }

  getDistance(block) {
    // Dragged block's top or bottom can snap to this
    const top = Math.abs(block.top - this.top)
    const bottom = Math.abs(block.bottom - this.top)
    // Which ever is closest
    return Math.min(top, bottom)
  }
}

class SnapAlignCenter extends SnapPoint {
  constructor({ id, top }) {
    super()

    this.blockIds = [id]
    this.top = top
    this.getGuides = draggedBlock => [
      {
        type: 'hRule',
        top
      }
    ]
  }

  getTop(layout) {
    return this.top - layout.height / 2
  }

  getDistance(block) {
    return Math.abs(block.vCenter - this.top)
  }
}

class SnapAlignTop extends SnapPoint {
  constructor({ id, top, guide2 }) {
    super()

    this.blockIds = [id]
    this.top = top
    let guides = [
      {
        type: 'hRule',
        top
      }
    ]
    if (guide2) {
      guides.push({
        type: 'hRule',
        top: guide2
      })
    }

    this.getGuides = () => guides
  }
}

class SnapEqSpace extends SnapPoint {
  getDistance(block) {
    if (block.overlapsH(this.targetBlock)) return Math.abs(block.top - this.top)
  }
}

class SnapEqAbove extends SnapEqSpace {
  // 🔲
  // |
  // 🔲
  // |
  // ⬛️

  constructor({ targetBlock, aboveBlock }) {
    super()

    const distance = targetBlock.top - aboveBlock.bottom

    this.blockIds = [targetBlock.id, aboveBlock.id]
    this.targetBlock = targetBlock
    this.top = targetBlock.bottom + distance
    this.getGuides = draggedBlock => [
      {
        type: 'vDistance',
        length: distance,
        left: centerOverlap(
          targetBlock.left,
          targetBlock.right,
          draggedBlock.left,
          draggedBlock.right
        ),
        top: targetBlock.bottom
      },
      {
        type: 'hRule',
        top: targetBlock.bottom + distance
      },
      {
        type: 'vDistance',
        length: distance,
        left: centerOverlap(
          targetBlock.left,
          targetBlock.right,
          aboveBlock.left,
          aboveBlock.right
        ),
        top: aboveBlock.bottom
      }
    ]
  }
}

class SnapEqAboveAdjcent extends SnapEqSpace {
  // Super Class for SnapEqAboveLeft & SnapEqAboveRight
  // 🔲 - 🔲     🔲 - 🔲
  //      |      |
  //      ⬛️    ⬛️

  constructor({ aboveBlock, adjcentBlock, distance }) {
    super()
    this.blockIds = [aboveBlock.id, adjcentBlock.id]
    this.targetBlock = aboveBlock
    this.top = aboveBlock.bottom + distance
    this.getGuides = draggedBlock => [
      {
        type: 'vDistance',
        left: centerOverlap(
          aboveBlock.left,
          aboveBlock.right,
          draggedBlock.left,
          draggedBlock.right
        ),
        top: aboveBlock.bottom,
        length: distance
      },
      {
        type: 'hRule',
        top: aboveBlock.bottom + distance
      }
      // Others added by subclasses
    ]
  }
}

class SnapEqAboveLeft extends SnapEqAboveAdjcent {
  // 🔲 - 🔲
  //      |
  //     ⬛️

  constructor({ aboveBlock, adjcentBlock }) {
    const distance = aboveBlock.left - adjcentBlock.right

    super({ aboveBlock, adjcentBlock, distance })

    this.superGuides = this.getGuides
    this.getGuides = draggedBlock =>
      this.superGuides(draggedBlock).concat([
        {
          type: 'hDistance',
          left: adjcentBlock.right,
          top: centerOverlap(
            aboveBlock.top,
            aboveBlock.bottom,
            adjcentBlock.top,
            adjcentBlock.bottom
          ),
          length: distance
        }
      ])
  }
}

class SnapEqAboveRight extends SnapEqAboveAdjcent {
  // 🔲 - 🔲
  //  |
  // ⬛️

  constructor({ aboveBlock, adjcentBlock }) {
    const distance = adjcentBlock.left - aboveBlock.right

    super({ aboveBlock, adjcentBlock, distance })

    this.superGuides = this.getGuides
    this.getGuides = draggedBlock =>
      this.superGuides(draggedBlock).concat([
        {
          type: 'hDistance',
          left: aboveBlock.right,
          top: centerOverlap(
            aboveBlock.top,
            aboveBlock.bottom,
            adjcentBlock.top,
            adjcentBlock.bottom
          ),
          length: distance
        }
      ])
  }
}

class SnapEqAdjcentAbove extends SnapEqSpace {
  // Super Class for SnapEqLeftAbove & SnapEqRightAbove
  //     🔲     🔲
  //      |     |
  // 🔲 - ⬛️    ⬛️ - 🔲

  constructor({ draggedBlock, aboveBlock, adjcentBlock, distance }) {
    super()
    this.blockIds = []
    this.targetBlock = aboveBlock
    this.top = aboveBlock.bottom + distance
    this.getGuides = draggedBlock => [
      {
        type: 'vDistance',
        length: distance,
        left: centerOverlap(
          aboveBlock.left,
          aboveBlock.right,
          draggedBlock.left,
          draggedBlock.right
        ),
        top: aboveBlock.bottom
      },
      {
        type: 'hRule',
        top: aboveBlock.bottom
      }
      // Others added by subclasses
    ]
  }
}

class SnapEqLeftAbove extends SnapEqAdjcentAbove {
  //     🔲
  //      |
  // 🔲 - ⬛️

  constructor({ draggedBlock, aboveBlock, adjcentBlock }) {
    const distance = draggedBlock.left - adjcentBlock.right

    super({ draggedBlock, aboveBlock, adjcentBlock, distance })

    this.superGuides = this.getGuides
    this.getGuides = draggedBlock =>
      this.superGuides(draggedBlock).concat([
        {
          type: 'hDistance',
          length: distance,
          left: adjcentBlock.right,
          top: centerOverlap(
            draggedBlock.top,
            draggedBlock.bottom,
            adjcentBlock.top,
            adjcentBlock.bottom
          )
        },
        {
          type: 'vRule',
          left: adjcentBlock.right
        }
      ])
  }
}

class SnapEqRightAbove extends SnapEqAdjcentAbove {
  // 🔲
  //  |
  // ⬛️ - 🔲

  constructor({ draggedBlock, aboveBlock, adjcentBlock }) {
    const distance = adjcentBlock.left - draggedBlock.right

    super({ draggedBlock, aboveBlock, adjcentBlock, distance })

    this.superGuides = this.getGuides
    this.getGuides = draggedBlock =>
      this.superGuides(draggedBlock).concat([
        {
          type: 'hDistance',
          length: distance,
          left: draggedBlock.right,
          top: centerOverlap(
            draggedBlock.top,
            draggedBlock.bottom,
            adjcentBlock.top,
            adjcentBlock.bottom
          )
        },
        {
          type: 'vRule',
          left: adjcentBlock.left
        }
      ])
  }
}

export default class Snapper {
  blocks = []

  calcSnapPoints(blocks, sections) {
    this.blocks = blocks
    this.snapPoints = []

    for (const section of sections.values()) {
      // 1col from top of section
      this.snapPoints.push(
        new SnapAlignTop({ top: section.top + 1 })
      )
      this.snapPoints.push(
        new SnapAlign({ top: section.top + section.height - 1 })
      )
    }

    Object.values(blocks).forEach(({ layout, id }) => {
      const block = new Rectangle(layout, id)

      // add alignment snap points
      this.snapPoints = this.snapPoints.concat([
        new SnapAlign({ id, top: block.top }),
        new SnapAlign({ id, top: block.bottom }),
        new SnapAlignCenter({ id, top: block.vCenter }),
        new SnapAlignTop({
          id,
          top: block.bottom + 1,
          guide2: block.bottom
        })
      ])

      // add equal spacing snap points
      // based on spacing between this block and others
      Object.values(blocks).forEach(({ layout, id }) => {
        if (id === block.id) return // not this block
        const otherBlock = new Rectangle(layout, id)

        if (otherBlock.isLeftOf(block)) {
          this.snapPoints = this.snapPoints.concat([
            new SnapEqAboveLeft({
              aboveBlock: block,
              adjcentBlock: otherBlock
            }),
            // if otherBlock is left of block then block must be right of otherBlock
            new SnapEqAboveRight({
              aboveBlock: otherBlock,
              adjcentBlock: block
            })
          ])
        }

        if (otherBlock.isAbove(block)) {
          this.snapPoints.push(
            new SnapEqAbove({ targetBlock: block, aboveBlock: otherBlock })
          )
        }
      })
    })
  }

  /**
   * Some snap points can't be pre-calculated, they must be done based on the 
   * current position of the dragged block
   * @param {Block} draggedBlock 
   * TODO: These proably dont need to be dynamic. We can snap corner of block to a pre-calculated 45º line
   */
  getDynamicSnapPoints(draggedBlock) {
    let snapPoints = []
    let blocksAbove = []
    let blocksBeside = []
    Object.values(this.blocks).forEach(({ layout, id }) => {
      if (id === draggedBlock.id) return // not this block
      const block = new Rectangle(layout, id)

      const above = block.isAbove(draggedBlock)
      if (above) {
        blocksAbove.push({
          block,
          distance: above
        })
        // check the blocks we already know are beside this one
        blocksBeside.forEach(beside => {
          const distance = Math.abs(above - beside.distance)
          if (distance < snapThreshold) {
            const SnapType =
              beside.side === 'left' ? SnapEqLeftAbove : SnapEqRightAbove
            snapPoints.push(
              new SnapType({
                draggedBlock: draggedBlock,
                adjcentBlock: beside.block,
                aboveBlock: block
              })
            )
          }
        })
      }

      const left = block.isLeftOf(draggedBlock)
      const beside = left || draggedBlock.isLeftOf(block)
      if (beside) {
        blocksBeside.push({
          block,
          side: left ? 'left' : 'right',
          distance: beside
        })
        // check the blocks we already know are above this one
        blocksAbove.forEach(above => {
          const distance = Math.abs(beside - above.distance)
          if (distance < snapThreshold) {
            // potential snap
            const SnapType = left ? SnapEqLeftAbove : SnapEqRightAbove
            snapPoints.push(
              new SnapType({
                draggedBlock: draggedBlock,
                adjcentBlock: block,
                aboveBlock: above.block
              })
            )
          }
        })
      }
    })

    return snapPoints
  }

  getVerticalSnap({ layout, id }) {
    const block = new Rectangle(layout, id)
    let snap = { distance: Infinity }

    // look for a snap calculated off current drag position
    const dynamicSnapPoints = this.getDynamicSnapPoints(block)

    // find closest of static snap points
    this.snapPoints.concat(dynamicSnapPoints).forEach(snapPoint => {
      if (snapPoint.blockIds.includes(id)) return // exclude guides from the block being dragged

      let distance = snapPoint.getDistance(block)
      if (distance && distance < snap.distance) {
        snap = {
          distance,
          point: snapPoint
        }
      }
    })

    if (snap.distance < snapThreshold) {
      const top = snap.point.getTop(layout)
      const height = block.height  // Remember height
      block.top = top // move top
      block.height = height // preserve height (move bottom)
      return {
        layout: block,
        guides: snap.point.getGuides(block)
      }
    } else {
      return {
        layout: block,
        guides: []
      }
    }
  }
}
